import React from 'react';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { ShortsClip } from '../../services/api';

type Job = NonNullable<ShortsClip['render_job']>;

/**
 * What stage a re-render is at, on the clip row in the batch view.
 *
 * This is the only place someone sees a render's state without opening that
 * clip's framing editor. Before it existed, a render that failed on the Mac
 * was invisible here: you clicked Queue, the batch view looked identical
 * forever, and the error sat in a dialog nobody had a reason to reopen.
 *
 * The stages are the ones the job record actually carries — queued (waiting
 * for the agent to poll), rendering (claimed), then done/failed/conflict. No
 * percentage, because REAPER reports none and inventing one would be a lie.
 */
const STAGES: Record<string, {
  label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning';
  hint: string;
}> = {
  queued: {
    label: 'RE-RENDER QUEUED', color: 'info',
    hint: 'Waiting for the Mac agent to pick it up — it polls every minute.',
  },
  rendering: {
    label: 'RENDERING', color: 'info',
    hint: 'The Mac has it and is rendering now.',
  },
  done: {
    label: 'RE-RENDERED', color: 'success',
    hint: 'The new render landed and this clip now points at it.',
  },
  failed: {
    label: 'RENDER FAILED', color: 'error',
    hint: 'The render did not complete. The clip still points at the old file.',
  },
  conflict: {
    label: 'RENDER CONFLICT', color: 'warning',
    hint: 'REAPER re-exported this region while the edit was open, so nothing '
      + 'was rendered rather than overwriting that work. Reopen to reload.',
  },
};

const when = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined,
    { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

const RenderJobBadge: React.FC<{ job?: Job | null }> = ({ job }) => {
  if (!job) return null;
  const stage = STAGES[job.status] ?? {
    label: job.status.toUpperCase(), color: 'default' as const, hint: '',
  };
  const stamp = when(job.finished_at ?? job.claimed_at ?? job.requested_at);

  return (
    <Tooltip
      title={
        <Stack spacing={0.5} sx={{ maxWidth: 360 }}>
          <Typography variant="caption">{stage.hint}</Typography>
          {stamp && (
            <Typography variant="caption" color="grey.400">{stamp}</Typography>
          )}
          {job.error && (
            // The failure reason belongs where the failure is shown. Hiding it
            // behind another click is how a PermissionError on the Mac went
            // unnoticed.
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {job.error.slice(0, 400)}
            </Typography>
          )}
          {job.attempts > 1 && (
            <Typography variant="caption">attempt {job.attempts}</Typography>
          )}
        </Stack>
      }
    >
      <Chip
        size="small"
        variant={job.status === 'done' ? 'filled' : 'outlined'}
        color={stage.color}
        label={stage.label}
        sx={{ fontSize: 10, height: 20 }}
      />
    </Tooltip>
  );
};

export default RenderJobBadge;
